import type { FC, ReactNode } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import { Check } from '@mui/icons-material'
import { useCopyToClipboard } from '@/common/hooks'

interface CopyableTextProps {
  readonly text: string
  readonly children: ReactNode
  readonly showCheckmark?: boolean
  readonly containerSx?: SxProps<Theme>
  readonly textSx?: SxProps<Theme>
  readonly checkmarkSx?: SxProps<Theme>
}

export const CopyableText: FC<CopyableTextProps> = ({
  text,
  children,
  showCheckmark = true,
  containerSx,
  textSx,
  checkmarkSx,
}) => {
  const { copied, copy } = useCopyToClipboard()

  const handleClick = useCallback(() => {
    void copy(text)
  }, [copy, text])

  return (
    <Box sx={containerSx ?? defaultContainerSx}>
      <Box component="span" onClick={handleClick} sx={textSx ?? defaultTextSx}>
        {children}
      </Box>
      {showCheckmark && copied && <Check sx={checkmarkSx ?? defaultCheckmarkSx} />}
    </Box>
  )
}

const defaultContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const defaultTextSx: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}

const defaultCheckmarkSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  color: 'success.main',
}
